import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { track, EVENTS } from "../lib/analytics";
import { HeroSpotlight } from "./HeroSpotlight";

export const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} aria-hidden="true" />;
};

export const Container = ({ className = "", children }) => (
  <div className={`container mx-auto px-6 md:px-10 lg:px-16 ${className}`}>{children}</div>
);

export const Eyebrow = ({ children, dark, className = "" }) => (
  <div className={`eyebrow inline-flex items-center gap-2 ${dark ? "text-[#00D4FF]" : "text-[#1A6EFF]"} ${className}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${dark ? "bg-[#00D4FF]" : "bg-[#1A6EFF]"}`} />{children}
  </div>
);

export function SectionHeader({ eyebrow, title, desc, dark, align = "left", className = "" }) {
  return (
    <div className={`${align === "center" ? "text-center mx-auto" : ""} max-w-3xl ${className}`}>
      {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
      <h2 className={`mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] ${dark ? "text-white" : "text-[#0A1628]"}`}>{title}</h2>
      {desc && <p className={`mt-5 text-base md:text-lg leading-relaxed ${dark ? "text-white/65" : "text-muted-foreground"}`}>{desc}</p>}
    </div>
  );
}

export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const kids = el.querySelectorAll(".reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    kids.forEach((k) => io.observe(k));
    return () => io.disconnect();
  }, []);
  return ref;
}

export function CtaButton({ to, children, variant = "primary", testId, label, className = "", ...rest }) {
  const styles = {
    primary: "bg-[#1A6EFF] hover:bg-[#0F5AE0] text-white shadow-[0_8px_24px_rgba(26,110,255,0.35)]",
    outline: "border border-white/30 text-white hover:bg-white/10 bg-transparent",
    light: "bg-white text-[#0A1628] hover:bg-white/90",
    ghost: "border border-[#0A1628]/15 text-[#0A1628] hover:border-[#1A6EFF] hover:text-[#1A6EFF] bg-transparent",
  };
  return (
    <Link to={to} onClick={() => track(EVENTS.CTA_CLICK, { label: label || children, to })} data-testid={testId}>
      <Button className={`h-12 px-7 rounded-full font-semibold transition-transform duration-300 hover:-translate-y-0.5 ${styles[variant]} ${className}`} {...rest}>
        {children}
      </Button>
    </Link>
  );
}

export function PageHero({ eyebrow, title, desc, children, compact, photo }) {
  return (
    <section className={`relative overflow-hidden bg-navy text-white ${compact ? "py-20 lg:py-24" : "py-24 lg:py-32"}`}>
      {photo && <HeroSpotlight photos={photo} />}
      <div className="absolute inset-0 bg-grid-dark opacity-30" />
      <div className="absolute -top-40 right-0 h-[480px] w-[480px] rounded-full bg-[#1A6EFF]/25 blur-[120px] orb-drift" />
      <div className="absolute -bottom-40 left-1/4 h-[360px] w-[360px] rounded-full bg-[#00D4FF]/15 blur-[120px] float-diagonal" />
      <Container className="relative">
        <div className="max-w-3xl">
          {eyebrow && <Eyebrow dark>{eyebrow}</Eyebrow>}
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] drop-shadow-[0_4px_24px_rgba(10,22,40,0.7)]">{title}</h1>
          {desc && <p className="mt-6 text-base md:text-lg text-white/75 leading-relaxed max-w-2xl">{desc}</p>}
          {children && <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
        </div>
      </Container>
    </section>
  );
}

export function FinalCta({ title = "Ready to Turn Technology Into Business Growth?", desc = "Tell us about the outcome you need. We will respond with a clear next step.", primary = "Book a Consultation", secondary = "Start a Conversation" }) {
  return (
    <section className="relative overflow-hidden bg-navy text-white py-24 lg:py-32">
      <div className="absolute inset-0 bg-grid-dark opacity-30" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[900px] rounded-full bg-[#1A6EFF]/20 blur-[140px]" />
      <Container className="relative text-center">
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] max-w-3xl mx-auto">{title}</h2>
        <p className="mt-5 text-base md:text-lg text-white/65 max-w-xl mx-auto">{desc}</p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <CtaButton to="/contact" testId="final-cta-primary">{primary} <Icons.ArrowRight className="ml-2 h-4 w-4" /></CtaButton>
          <CtaButton to="/contact?form=employer" variant="outline" testId="final-cta-secondary">{secondary}</CtaButton>
        </div>
      </Container>
    </section>
  );
}
